import {
	useGetClientPropertiesQuery,
	useLazyCheckClientExistsInInsightQuery,
} from '@/features/clients/clientsApi';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { buildValidationSchema, mapFieldsToDefaults, VIEW_TYPES } from '@/features/config/helper';
import { useListContactsQuery } from '@/features/contacts/contactsApi';
import { contactOrder, parseContactToInsight } from '@/features/contacts/helper';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetDriveItemQuery, useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { isProspectFieldVisible } from '@/features/config/helper';

const INITIAL_CONTEXT = {
	view: VIEW_TYPES.create,
	variant: null,
	schema: {},
	isProspect: false,
	setIsProspect: (value, loadingTime) => {},
	initialValues: {},
	isLoading: false,
	fieldDensity: 'page',
};

export const ClientSchemaContext = createContext(INITIAL_CONTEXT);

export const useClientSchemaContext = () => {
	const context = useContext(ClientSchemaContext);
	if (!context) {
		throw new Error('useClientSchemaContext must be used within a ClientSchemaContextProvider');
	}
	return context;
};

const ClientSchemaProvider = ({ view, variant, contactData, children }) => {
	const params = useParams();
	const location = useLocation();

	const clientExistsCacheRef = useRef(new Map());

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const dialogData = useSelector((state) => state.dialog);
	const dialogOpen = dialogData?.open?.clientDialog;
	const data = dialogData?.data?.clientDialog;
	const clientId =
		view === VIEW_TYPES.update
			? (params?.clientId ?? dialogData?.data?.editContact?.clientId ?? data?.clientId)
			: null;

	// Check for both dialog state and location state
	const initialIsProspect = location?.state?.isProspect ?? data?.isProspect ?? false;

	const fieldDensity = dialogOpen ? 'dialog' : 'page';

	const [prospectChangeLoading, setProspectChangeLoading] = useState(false);

	const {
		data: userDetails,
		isLoading,
		isUninitialized,
		isError,
	} = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [checkClientExists] = useLazyCheckClientExistsInInsightQuery();

	const handleCheckClientExists = useCallback(
		// userId is the Firestore doc id of the user whose brokerage to search (the
		// client's owner, else the current user) — checkClientExistsInInsight sends it.
		async ({ name, userId, insightId }) => {
			const trimmedName = name?.trim();
			if (!trimmedName || !userId) return true;

			const cacheKey = JSON.stringify({
				name: trimmedName,
				userId,
				insightId: insightId ?? null,
			});

			console.log('Client exists cache', clientExistsCacheRef.current);
			if (clientExistsCacheRef.current.has(cacheKey)) {
				return clientExistsCacheRef.current.get(cacheKey);
			}

			const response = await checkClientExists({
				companyName: trimmedName,
				userId,
			})
				.unwrap()
				.then((res) => {
					const existingClients = res.filter((r) => r.Id !== insightId);
					return existingClients.length === 0;
				})
				.catch((err) => {
					console.error('Error getting client exists', err);
					return true;
				});
			clientExistsCacheRef.current.set(cacheKey, response);
			return response;
		},
		[checkClientExists],
	);

	const brokerGroupIds = userDetails.brokerGroupArray ?? [];

	const ledger = useMemo(() => userDetails?.broker?.ledger ?? '', [userDetails?.broker?.ledger]);

	const [isProspect, setIsProspect] = useState(initialIsProspect);

	const fieldsQuery = useGetFormConfigQuery({ configType: 'client' }, { skip: !userDetails });

	const visibleFields = useMemo(
		() =>
			(fieldsQuery.data ?? []).filter((f) =>
				isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view],
			),
		[fieldsQuery.data, isProspect, view],
	);

	const { data: clientData, ...clientQuery } = useGetClientPropertiesQuery(clientId, {
		skip: !clientId,
	});

	const client = view === VIEW_TYPES.create ? null : clientData;
	const clientRow = client?.clientRow;

	useEffect(() => {
		if (clientRow?.isProspect != null && clientRow.isProspect !== isProspect) {
			setIsProspect(clientRow.isProspect);
		}
	}, [clientRow?.isProspect]);

	const clientFolderDriveId = clientRow?.clientFolderDriveId;

	const { currentData: driveItem, ...driveItemQuery } = useGetDriveItemQuery(
		{
			driveId: clientFolderDriveId,
			itemId: clientRow?.driveId,
		},
		{ skip: !clientRow?.driveId || !clientFolderDriveId },
	);

	const { currentData: parentFolders, ...parentFolderFilesQuery } = useGetFoldersAndFilesQuery(
		{
			itemId: driveItem?.parentReference?.id,
			driveId: clientFolderDriveId,
			recursive: false,
		},
		{ skip: !driveItem?.parentReference?.id || !clientFolderDriveId },
	);

	const folders = useMemo(
		() =>
			(parentFolders?.folders ?? [])
				.filter((f) => f.id !== driveItem?.id)
				.map((f) => {
					try {
						return decodeURI(f.name.normalize());
					} catch (e) {
						console.log('Error decode URI', e);
						return f.name.normalize();
					}
				}),
		[parentFolders, driveItem],
	);

	const defaultClientValues = useMemo(
		() =>
			client
				? {
						firebase: clientRow,
						insight: {
							...(clientRow?.insight ?? {}),
							...(client.insight ?? {}),
						},
					}
				: null,
		[client, clientRow],
	);

	const contactQuery = useListContactsQuery(clientRow?.clientId, {
		skip: !clientRow?.clientId,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});

	const normaliseContacts = (contactArray = []) => {
		return contactArray
			.map(parseContactToInsight)
			.sort(
				(a, b) =>
					contactOrder(b) - contactOrder(a) ||
					(a?.Name ?? a?.Addressee ?? '').localeCompare(b?.Name ?? b?.Addressee ?? ''),
			);
	};

	const contacts = useMemo(() => {
		if (view === VIEW_TYPES.create || variant === 'addContact') {
			return null;
		}

		const data =
			variant === 'editContact' && contactData ? [contactData] : contactQuery.data?.slice();

		if (!data) return null;

		console.log('Client schema contacts', data);
		return normaliseContacts(data);
	}, [view, contactQuery.data, variant, contactData]);

	useEffect(() => console.log('[clientSchemaContext] Contacts', contacts), [contacts]);

	const ownerQuery = useGetUserDetailsQuery(
		{ userId: clientRow?.ownerDocId },
		{
			skip: !clientRow?.ownerDocId,
		},
	);
	const ownerUserId = clientRow?.ownerDocId ?? userDetails?.userId;

	const fields = useMemo(() => {
		return visibleFields.map((f) => ({
			...f,
			required: f.mandatory?.[isProspect ? 'prospect' : 'client'],
		}));
	}, [visibleFields, isProspect]);

	const schema = useMemo(() => {
		return buildValidationSchema({
			fields,
			view,
			context: {
				brokerGroupIds,
				userId: ownerUserId,
				checkClientExists: handleCheckClientExists,
				variant,
				folders,
			},
		});
	}, [fields, view, brokerGroupIds, ownerUserId, variant, handleCheckClientExists, folders]);

	const handleChangeProspect = useCallback((newValue, loadingTime) => {
		setIsProspect(Boolean(newValue));
		setProspectChangeLoading(true);
		setTimeout(() => {
			setProspectChangeLoading(false);
		}, loadingTime ?? 1);
	}, []);

	useEffect(() => console.log('Existing client', { client, schema }), [client, schema]);

	const fieldDefaults = useMemo(
		() => ({
			...mapFieldsToDefaults({
				fields,
				view,
				user: ownerQuery?.currentData ?? userDetails,
				existingClient: defaultClientValues,
				isProspect,
			}),
			...(contacts && { contacts }),
		}),
		[
			fields,
			view,
			ownerQuery?.currentData,
			userDetails,
			defaultClientValues,
			contacts,
			isProspect,
		],
	);

	const initialValues = useMemo(() => {
		const vals = {
			contacts: [
				{
					isMainContact: true,
				},
			],
			...fieldDefaults,
			...(isProspect && { ClientTypeId: 1 }),
		};

		return variant === 'addContact' ? { contacts: [{ isMainContact: false }] } : vals;
	}, [fieldDefaults, isProspect, variant]);

	useEffect(
		() =>
			console.log(`Initial values for ${view} ${isProspect ? 'prospect' : 'client'}`, {
				initialValues,
				fieldDefaults,
				contacts,
			}),
		[initialValues, view, isProspect, fieldDefaults, contacts],
	);

	const isContextLoading =
		isLoading ||
		isUninitialized ||
		fieldsQuery.isLoading ||
		fieldsQuery.isUninitialized ||
		prospectChangeLoading ||
		clientQuery.isLoading;

	const contextValue = useMemo(
		() => ({
			view,
			variant,
			schema,
			isProspect,
			setIsProspect: handleChangeProspect,
			initialValues,
			isLoading: isContextLoading,
			fieldDensity,
		}),
		[
			view,
			variant,
			schema,
			isProspect,
			handleChangeProspect,
			initialValues,
			isContextLoading,
			fieldDensity,
		],
	);

	return (
		<ClientSchemaContext.Provider value={contextValue}>{children}</ClientSchemaContext.Provider>
	);
};

export default ClientSchemaProvider;
